import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, Chip, Stack, Typography} from 'views/components';
import {translate} from 'views/module';

import {getParticipantRns} from '../communication/communicationUtils';

const formatUpdatedAt = (updatedAt) => {
  const updatedAtDate = new Date(updatedAt);

  if (Number.isNaN(updatedAtDate.getTime())) {
    return '';
  }

  return updatedAtDate.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getPriorityLabel = (caseData) => {
  const priority = caseData.priority;

  if (priority !== null && priority !== undefined && priority !== '') {
    return translate('case.List.PriorityLabel', {priority});
  }

  return translate('case.List.PriorityUnprioritized');
};

const getStatusLabel = (status) => {
  const listStatusLabel = translate(`case.List.StatusDisplay.${status}`);

  return listStatusLabel === 'Missing translation' ?
    translate(`case.Status.${status}.Title`) :
    listStatusLabel;
};

const CaseInboxItem = (props) => {
  const {
    caseData,
    isSelected,
    onSelect,
  } = props;

  const participantLabels = getParticipantRns(caseData)
      .filter((participantRn) => typeof participantRn === 'string' && participantRn);
  const updatedAtLabel = formatUpdatedAt(caseData.updatedAt);
  const priorityLabel = getPriorityLabel(caseData);

  return (
    <ButtonBase
      onClick={() => onSelect(caseData.caseRn)}
      sx={{
        'display': 'block',
        'width': '100%',
        'textAlign': 'left',
        'border': '1px solid',
        'borderColor': isSelected ? '#EB7A58' : '#ECECF1',
        'borderRadius': '9px',
        'backgroundColor': isSelected ? '#FFF4EE' : '#FFFFFF',
        'p': '13px 14px',
        '&:hover': {
          backgroundColor: isSelected ? '#FFF1EA' : '#F7F8FA',
          borderColor: isSelected ? '#EB7A58' : '#DCDDE5',
        },
      }}
      type='button'
    >
      <Stack sx={{gap: 0.9, width: '100%'}}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            width: '100%',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              lineHeight: '16px',
              color: '#969BB0',
              wordBreak: 'break-word',
            }}
          >
            {caseData.caseRn}
          </Typography>
          <Chip
            label={priorityLabel}
            size='small'
            sx={{
              'height': '20px',
              'fontSize': '11px',
              'fontWeight': 600,
              'color': '#526071',
              'backgroundColor': '#F7F8FA',
              'border': '1px solid #E4E5EA',
              'borderRadius': '5px',
              '& .MuiChip-label': {
                px: '7px',
              },
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: 1.35,
            color: '#050A14',
            wordBreak: 'break-word',
          }}
        >
          {caseData.caseType}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1.5,
            color: '#63708A',
            wordBreak: 'break-word',
          }}
        >
          {participantLabels.join(', ') || translate('case.Detail.EmptyValue')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            width: '100%',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: caseData.status === 'COMPLETE' ? '#008A00' : '#B77900',
            }}
          >
            {getStatusLabel(caseData.status)}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color: '#A59AB5',
            }}
          >
            {updatedAtLabel}
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  );
};

CaseInboxItem.propTypes = {
  caseData: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default React.memo(CaseInboxItem);
